import DatePicker from "react-datepicker";
import CancelIcon from "@mui/icons-material/Cancel";
import { IOption } from "Dashboard/domain/IOption";
import { IBuildingWithSalesUnits } from "Dashboard/scenes/HighRiseSales/domain/IBuildingWithSalesUnits";
import AddIcon from "@mui/icons-material/Add";
import { ISalesUnit } from "Dashboard/scenes/HighRiseSales/domain/ISalesUnit";
import SalesUnitRow from "./SalesUnitRow";
import { useAppDispatch } from "hooks/hooks";
import {
  selectState,
  setSelectedBuilding,
  setNewSalesUnitPopover,
  setExpandingBuilding,
  setSalesUnit,
  setDeleteBuildingModalOpen,
  updateBuildingWithSalesUnits,
} from "../main.slice";
import { useSelector } from "react-redux";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { DiffDays, FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { IBuildingUpdate } from "Dashboard/scenes/HighRiseSales/domain/IBuildingUpdate";
import { ISalesUnitUpdate } from "Dashboard/scenes/HighRiseSales/domain/ISalesUnitUpdate";
import { styles } from "Dashboard/scenes/HighRiseSales/components/SelectStyle";
import { debounce } from "lodash";
import { hrsService } from "Dashboard/scenes/HighRiseSales/services/hrs.services";
import EmployeeSelect from "Dashboard/scenes/Resources/components/EmployeeSelect";
import { useMemo } from "react";
import InfoTooltip from "components/Popup/InfoTooltip";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { HIGH_RISE_SALES_MODULE_NAME } from "Dashboard/scenes/HighRiseSales/domain/HighRiseSalesModule";
import { grey } from "@mui/material/colors";
import { debounceTimeout } from "utils/debounceUtil";

interface IProps {
  building: IBuildingWithSalesUnits;
  index: number;
  employees: IOption[];
  salesUnitTypes: IOption[];
  updateBuilding: (data: IBuildingUpdate) => void;
}

const TableRow = ({
  building,
  index,
  employees,
  salesUnitTypes,
  updateBuilding,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);
  const { selectedBuilding } = useSelector(selectState);

  const selectBuilding = (index: number) => {
    dispatch(setExpandingBuilding(building.building.buildingId));
    dispatch(setSelectedBuilding(index));
  };

  const onInputCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateBuilding({
      buildingId: building.building.buildingId,
      [event.target.name]: event.target.checked,
    });
  };

  const delayedUpdateSalesUnit = useMemo(
    () =>
      debounce(async (data: ISalesUnitUpdate) => {
        try {
          const response = await hrsService.updateSalesUnit(data);
          dispatch(updateBuildingWithSalesUnits(response));
        } catch (err) {
          console.log(err);
        }
      }, debounceTimeout),
    [dispatch]
  );

  const updateSalesUnit = async (data: ISalesUnitUpdate) => {
    delayedUpdateSalesUnit(data);
  };

  return (
    <>
      <div className={`row${index === selectedBuilding ? " active" : ""}`}
        onClick={() => selectBuilding(index)}
      >
        <div className="col center">
          <input
            name="isFavourite"
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
            onChange={onInputCheckboxChange}
            checked={building.building.isFavourite
              ? building.building.isFavourite : false}
            disabled={!hasWriteAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)}
          />
        </div>
        <div className="col center">
          <span
            className="icon-item"
            onClick={(e) => {
              const targetElement = e.currentTarget;
              requestAnimationFrame(() => {
                const rect = targetElement.getBoundingClientRect();
                dispatch(setNewSalesUnitPopover({
                  isOpen: true,
                  left: rect.left + rect.width / 2,
                  top: rect.top + rect.height / 2
                }));
              });
            }}
          >
            <AddIcon className="fas" />
          </span>
        </div>
        <div className="col strong">
          <div className="bg-item overflowMinWidth">
            <InfoTooltip tooltip={building.building.fullName}
              classesProp="bg-text-overflow">
              {building.building.fullName}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center">
          {hasFullAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)
            ? <span onClick={(e) => {
              e.stopPropagation();
              dispatch(
                setDeleteBuildingModalOpen({ visible: true, index: index })
              );
            }}
            >
              <CancelIcon
                sx={{
                  cursor: "pointer",
                  fontSize: 15,
                  color: index === selectedBuilding ? grey[100] : grey[400],
                  '&:hover': { color: "darkred" },
                  transition: 'all 0.2s ease-in-out',
                }}
              />
            </span> : <></>}
        </div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div className="col light strong">
          <div className="bg-item bg-item-border-dotted center">
            {FormatDateShort(building.building.contractDate)}
          </div>
        </div>
        <div className="col">
          <div className="bg-item bg-item-border-dotted">
            <DatePicker
              className="center strong light text-medium"
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(startDate: any) => {
                updateBuilding({
                  buildingId: building.building.buildingId,
                  startDate: FormatDateIso(startDate),
                });
              }}
              selected={
                building.building.startDate != null
                  ? new Date(building.building.startDate)
                  : null
              }
            />
          </div>
        </div>
        <div className="col strong">
          <div className="bg-item bg-item-border-dotted center">
            {FormatDateShort(building.building.dueDate)}
          </div>
        </div>
        <div className="col strong">
          <div className="bg-item bg-item-border-dotted center">
            {building.building.dueDate &&
              DiffDays(building.building.dueDate) + " dana"}
          </div>
        </div>
        <div className="col">
          <div className="bg-item bg-item-dark">
            <DatePicker
              className="center strong text-medium"
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => {
                updateBuilding({
                  buildingId: building.building.buildingId,
                  finishDate: FormatDateIso(date),
                });
              }}
              selected={
                building.building.finishDate != null
                  ? new Date(building.building.finishDate)
                  : null
              }
            />
          </div>
        </div>
        <InfoTooltip tooltip="Prikaži na mobilnom uređaju"
          classesProp="col center">
          <input
            disabled={!hasWriteAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)}
            name="forMobileApp"
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
            onChange={onInputCheckboxChange}
            checked={building.building.forMobileApp
              ? building.building.forMobileApp : false}
          />
        </InfoTooltip>
        <InfoTooltip
          tooltip="Čekirane zgrade ne vide padajući meniji publishera"
          classesProp="col center"
        >
          <input
            name="hideInDropDowns"
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
            onChange={onInputCheckboxChange}
            checked={building.building.hideInDropDowns
              ? building.building.hideInDropDowns : false}
            disabled={!hasWriteAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)}
          />
        </InfoTooltip>
        <div className="col center">
          <EmployeeSelect
            customStyles={styles.customStylesPartner("", 10)}
            defaultValue={building.building.managerId}
            options={employees}
            onChange={(managerId: number) => {
              updateBuilding({
                buildingId: building.building.buildingId,
                managerId,
              });
            }}
          // error={errors.partnerId}
          />
        </div>
      </div>
      {building.salesUnits.map((salesUnit: ISalesUnit, index: number) => (
        <SalesUnitRow
          salesUnit={salesUnit}
          salesUnitTypes={salesUnitTypes}
          key={`${salesUnit.salesUnitId}`}
          index={index}
          updateSalesUnit={updateSalesUnit}
        />
      ))}
    </>
  );
};

export default TableRow;
